<template>
    <div
        class="w-full xl:w-10/12 mx-auto md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-x-10 gap-y-5 space-y-5 md:space-y-0"
    >
        <a
            v-for="(job, index) in jobs"
            :key="index"
            :title="job.title"
            :href="job.url"
            :target="settingLinkTarget"
            class="col-span-1 w-full h-auto bg-white shadow-topanimation border border-gray-300 block focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
        >
            <JobItem :job="job" />
        </a>
        <a
            v-if="vacancyCtaItemToggle"
            :href="vacancyCtaItemLink"
            class="col-span-1 w-full h-auto bg-white shadow-topanimation border border-gray-300 block focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
        >
            <div class="h-40 xl:h-52 w-full overflow-hidden">
                <img
                    :src="vacancyCtaItemImage"
                    sizes="
                (min-width: 1024px) 750px,
                (min-width: 400px) 500px,
                300px
            "
                    class="w-full h-full object-cover"
                    itemprop="image"
                />
            </div>
            <div class="px-5 py-3">
                <h3
                    class="font-heading uppercase text-base lg:text-lg mb-2"
                    itemprop="title"
                    v-text="vacancyCtaItemTitle"
                ></h3>
                <div v-text="vacancyCtaItemText"></div>
            </div>
        </a>

        <div class="flex justify-end col-span-2">
            <JobPagination :pagination="pagination" />
        </div>
    </div>
</template>

<script setup>
import axios from "axios";
import { onMounted, ref, watch, computed, inject } from "vue";
import JobItem from "@/components/Jobs/JobItem";
import JobPagination from "@/components/Jobs/JobPagination";
import { getCurrentLocale, isMultiLanguageSite } from "@/helpers/apiHelper";
import { statamicApiAssetToSrcSet } from "@/helpers/imageHelper";

const props = defineProps({
    settingLinkTarget: {
        type: String,
        required: true,
    },
    currentSite: {
        type: String,
        required: true,
    },
    vacancyCtaItemToggle: {
        type: Boolean,
        default: false,
    },
    vacancyCtaItemImage: {
        type: Object,
        required: true,
    },
    vacancyCtaItemTitle: {
        type: String,
        required: true,
    },
    vacancyCtaItemText: {
        type: String,
        required: true,
    },
    vacancyCtaItemLink: {
        type: String,
        required: true,
    },
});

const snippets = inject("statamic_snippets");

const categories = ref([]);
const countries = ref([]);
const durations = ref([]);
const loading = ref(false);
const enableFilterMenu = ref(false);
const activeFilters = ref({
    category: null,
    country: null,
    duration: null,
});

const activeShownFilters = ref({
    category: null,
    country: null,
    duration: null,
});

const jobs = ref([]);
const pagination = ref({
    from: 1,
    current_page: 1,
    per_page: 10,
    links: [],
});

const availableCategories = computed(() =>
    categories.value.filter((category) => jobs.value.find((job) => job.category?.id === category.id))
);

const availableCountries = computed(() =>
    countries.value.filter((country) => jobs.value.find((job) => job.country?.id === country.id))
);

const availableDurations = computed(() =>
    durations.value.filter((duration) => jobs.value.find((job) => job.job_duration?.id === duration.id))
);

const buildJobUrl = (url = "api/collections/job/entries") => {
    const currentUrl = new URL(`${window.location.origin}/${url}`);

    currentUrl.searchParams.append("limit", "10");
    currentUrl.searchParams.append("sort", "order");
    currentUrl.searchParams.append(
        "fields",
        "title,url,hero_image,small_description,period,period_short,country,salery,category,duration,job_durations,site"
    );

    if (activeFilters.value.category) {
        currentUrl.searchParams.append("filter[category]", activeFilters.value.category.slug);
    }

    if (activeFilters.value.country) {
        currentUrl.searchParams.append("filter[country]", activeFilters.value.country.slug);
    }

    if (props.currentSite && isMultiLanguageSite()) {
        currentUrl.searchParams.append("filter[site]", getCurrentLocale(props.currentSite));
    }

    if (activeFilters.value.duration) {
        currentUrl.searchParams.append("filter[job_duration]", activeFilters.value.duration.slug);
    }

    return currentUrl;
};

const getJobs = (url = null) => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    if (!url) {
        url = buildJobUrl();
    }

    axios.get(url).then(({ data: jobsData }) => {
        const { data, meta: jobsPagination } = jobsData;

        pagination.value.from = jobsPagination.from;
        pagination.value.current_page = jobsPagination.current_page;
        pagination.value.per_page = jobsPagination.per_page;
        pagination.value.links = jobsPagination.links;

        jobs.value = data;

        loading.value = false;
    });
};

const getCategories = () => {
    const url = `${window.location.origin}/api/taxonomies/category/terms`;

    axios.get(url).then(({ data }) => {
        const { data: categoriesData } = data;

        categories.value = categoriesData;
    });
};

const getCountries = () => {
    const url = `${window.location.origin}/api/taxonomies/countries/terms`;

    axios.get(url).then(({ data }) => {
        const { data: countriesData } = data;

        countries.value = countriesData;
    });
};

const getDuration = () => {
    const url = `${window.location.origin}/api/taxonomies/durations/terms`;

    axios.get(url).then(({ data }) => {
        const { data: durationData } = data;

        durations.value = durationData;
    });
};

const unsetActiveFilter = (key) => {
    activeFilters.value[key] = null;
};

const setFilters = () => {
    activeFilters.value.country = activeShownFilters.value.country;
    activeFilters.value.category = activeShownFilters.value.category;
    activeFilters.value.duration = activeShownFilters.value.duration;

    enableFilterMenu.value = !enableFilterMenu.value;
};
watch(
    activeFilters,
    () => {
        getJobs();
    },
    {
        deep: true,
    }
);

onMounted(() => {
    getJobs();
    getCategories();
    getCountries();
    getDuration();
});
</script>
